import classNames from 'classnames';
import useStyles from 'isomorphic-style-loader/useStyles';
import _startCase from 'lodash/startCase';
import { useMemo } from 'react';
import type { DropdownItemProps, SemanticICONS } from 'semantic-ui-react';
import { Dropdown } from 'semantic-ui-react';

import { darkReaderIgnoreInlineStyleClass } from '@components/dark-mode/dark-mode-provider';

import s from './abstract-status-dropdown.scss';

export type StatusVariant = 'primary' | 'secondary' | 'muted';

export interface StatusOption<V extends string> {
  /**
   * The status value used for state management and change handlers.
   */
  value: V;
  /**
   * The Semantic UI icon name displayed alongside the status.
   */
  icon: SemanticICONS;
  /**
   * Visual variant affecting the option's color scheme.
   */
  variant?: StatusVariant;
  /**
   * Display label shown in the dropdown. Defaults to startCased `value` if not
   * provided.
   */
  label?: string;
}

export interface AbstractStatusDropdownProps<Status extends string> {
  /**
   * Accessible label for the dropdown, surfaced as `aria-label` on the
   * underlying listbox. Useful when multiple status dropdowns can appear in
   * the same view.
   */
  'aria-label'?: string;
  /**
   * Blocks interaction and dims the dropdown with a muted background to signal
   * the action is unavailable.
   */
  disabled?: boolean;
  /**
   * Replaces the status icon with a spinner and blocks interaction while a
   * mutation is in flight.
   */
  loading?: boolean;
  /**
   * Callback fired when the user selects a new status from the dropdown.
   */
  onChange?: (newStatus: Status) => void;
  /**
   * Blocks interaction while keeping the dropdown's normal appearance.
   */
  readOnly?: boolean;
  /**
   * The current status value.
   */
  status: Status;
}

/**
 * An abstract component constructor that creates an `AbstractStatusDropdown`
 * variant, which returns a dropdown to manage an enumerable status. Uses
 * generic values internally, mapped to external values it is initialized with.
 *
 * Unlike most of our other dropdowns, this one is opinionated and intended to
 * serve a limited number of options, with a concise output that excludes the
 * current value from the list of options.
 *
 * @abstract
 * @constructor
 */
export const makeAbstractStatusDropdown = <V extends string>(
  options: StatusOption<V>[],
) => {
  const AbstractStatusDropdown = ({
    'aria-label': ariaLabel,
    disabled,
    loading,
    onChange,
    readOnly,
    status,
  }: AbstractStatusDropdownProps<V>) => {
    useStyles(s);

    const dropdownOptions = useMemo<DropdownItemProps[]>(
      () =>
        options.map(option => ({
          value: option.value,
          key: option.value,
          text: option.label ?? _startCase(option.value),
          icon: {
            name: option.icon,
            // We have to use inline styles here to override the icon color in
            // dark mode, otherwise it is inconsistent or invisible.
            className: darkReaderIgnoreInlineStyleClass,
            style: { color: 'var(--anchore-color-gray-900)' },
          },
          className: classNames(s.option, option.variant && s[option.variant]),
        })),
      [],
    );

    const triggerOption = dropdownOptions.find(
      option => option.value === status,
    );
    const isInteractive = !disabled && !readOnly && !loading;

    return (
      <Dropdown
        aria-label={ariaLabel}
        className={classNames(s.root, (disabled || loading) && s.disabled)}
        disabled={!isInteractive}
        value={status}
        onChange={(_, data) => isInteractive && onChange?.(data.value as V)}
        options={dropdownOptions}
        trigger={
          <Dropdown.Item
            {...triggerOption}
            icon={
              loading
                ? {
                    name: 'spinner',
                    loading: true,
                    className: darkReaderIgnoreInlineStyleClass,
                    style: { color: 'var(--anchore-color-gray-900)' },
                  }
                : triggerOption?.icon
            }
            key={undefined}
            active
          />
        }
        button
        compact
        floating
      />
    );
  };

  AbstractStatusDropdown.displayName = 'AbstractStatusDropdown';

  return AbstractStatusDropdown;
};
